/**
 * useTracking Hook
 * React hook for initializing and using tracking services
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFunnelConfig } from '@/context/FunnelConfigContext';
import { 
  initializeGoogleAnalytics, 
  initializeFacebookPixel,
  initializeGoogleAdsConversion,
  trackPageView,
  trackEvent,
  trackFormSubmission,
  trackMetaLead,
  trackMetaCustomEvent,
  trackButtonClick,
  trackConversion,
  trackContentView,
  getTrackingStatus
} from '@/services/tracking';

/**
 * Hook to initialize tracking on app mount
 * @param {Object} config - Funnel config object (optional, will use useFunnelConfig if not provided)
 * @param {boolean} loading - Loading state (optional)
 */
export const useTrackingInitialize = (config = null, loading = null) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  
  // Always call useFunnelConfig to maintain consistent hook order
  // Use passed config if available, otherwise use the hook result
  const funnelConfig = useFunnelConfig();
  const trackingConfig = config !== null ? config : funnelConfig.config;
  const trackingLoading = loading !== null ? loading : funnelConfig.loading;

  useEffect(() => {
    if (trackingLoading) {
      return; // Wait for config to load
    }

    const initializeTracking = () => {
      try {
        const tracking = trackingConfig?.tracking;

        if (!tracking) {
          setIsInitialized(true);
          return;
        }

        // Initialize Facebook Pixel if pixel ID is provided
        if (tracking.facebook_pixel_id) {
          initializeFacebookPixel(tracking.facebook_pixel_id);
        }

        if (tracking.meta_custom_event_name) {
          trackMetaCustomEvent(tracking.meta_custom_event_name);
        }

        // Initialize Google Ads Conversion Tracking if both ID and label are provided
        if (tracking.gtag_aw_conversion_id && tracking.gtag_aw_conversion_label) {
          initializeGoogleAdsConversion(
            tracking.gtag_aw_conversion_id,
            tracking.gtag_aw_conversion_label
          );
        }

        setIsInitialized(true);
      } catch (err) {
        console.error('Failed to initialize tracking:', err);
        setError(err.message);
        setIsInitialized(false);
      }
    };

    initializeTracking();
  }, [trackingConfig, trackingLoading]);

  return { isInitialized, error, trackingStatus: getTrackingStatus() };
};

/**
 * Hook to track page views automatically on route change
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);
};

/**
 * Hook that provides tracking functions
 * @returns {Object} Tracking functions
 */
export const useTracking = () => {
  return {
    trackEvent,
    trackFormSubmission,
    trackMetaLead,
    trackMetaCustomEvent,
    trackButtonClick,
    trackConversion,
    trackContentView,
    trackPageView,
    getTrackingStatus
  };
};

export default useTracking;

